import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Row, Col, ProgressBar, Button } from 'react-bootstrap';

import ReactGA4 from 'react-ga4';

import { getFile } from '../../../../util/file.js';

import { getVouchers, checkVoucherConditions, applyVoucher } from '../../VoucherActions.js';
import { getOrdersByUser } from '../../../Order/OrderActions.js';
import { getCart, getCartVouchers } from '../../../Cart/CartActions.js';
import { getCartTotals } from '../../../Cart/CartHelpers.js';
import { memoizedGetProducts } from '../../../Product/ProductActions.js';
import { getLoggedUser } from '../../../User/UserActions.js';
import { getUserGroup } from '../../../User/UserGroupActions.js';

import Icon from '../../../../components/Content/Icon.js';
import Price from '../../../Currency/components/view/Price.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import GamifiedButton from '../../../../components/Content/GamifiedButton.js';
import config from '../../../../config.js';

function VoucherProgress({ dispatch, user = null, userGroup = null, cart = null, cartTotals = null, cartVouchers = [], vouchers = [], products = [], orders = [], onCart = false, hideProgress = false, ctaAnchor = null }) {
    const [displayConditions, setDisplayConditions] = useState(false);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
		setMounted(true);
    }, []);

	if(!mounted) {
		return null;
	}

	if(cart.items.find(item => (item.flags || []).includes('fidelity'))) {
		return null;
	}

    const vouchersSelected = (vouchers || [])
        .filter(voucher => voucher.isPublic || voucher.isAuto)
        .concat(cartVouchers)
        .sort((voucherA, voucherB) => {
            if(voucherA.priority || voucherB.priority) {
                return (voucherA.priority || 0) - (voucherB.priority || 0);
            }
            const getMinSubtotalCondition = voucher => voucher.conditions.filter(condition => condition.key === 'subtotal').reduce((min, condition) => Math.min(condition.value, min || condition.value), null);
            return getMinSubtotalCondition(voucherA) - getMinSubtotalCondition(voucherB);
        });
    const nextVoucher = cart.isVoucherPrioritary ? (cartVouchers || [])[0] : vouchersSelected.find(voucher => voucher.conditions.find(condition => condition.key === 'subtotal' && condition.value > cartTotals.items));

	const handleClickCTA = () => {
		if(nextVoucher) {
			dispatch(applyVoucher(nextVoucher));
			if(typeof window !== 'undefined' && config.google.analytics.trackingCodeGA4) {
				// Ga: New
				const data = {
					creative_name: ctaAnchor,
					creative_slot: 'voucher_progress',
					promotion_id: nextVoucher?.code,
					promotion_name: nextVoucher?.name,
				};
				console.log('GA: Select promotion', data);
				ReactGA4.event('select_promotion', data);
			}
		}
	};

    // if(cartVouchers.length) {
    //     [nextVoucher] = cartVouchers;
    // }
    // console.log(vouchersSelected, nextVoucher);

    if(nextVoucher && checkVoucherConditions({ ...nextVoucher, conditions: nextVoucher.conditions.filter(condition => condition.key !== 'subtotal') }, user, userGroup, orders, cart)) {
        const nextVoucherStep = nextVoucher.conditions.filter(condition => condition.key === 'subtotal').reduce((step, condition) => Math.max(condition.value, step), 0);
        const nextVoucherProductAction = nextVoucher.actions.find(action => action.key === 'product');
        return (
            <div className="py-2">
				<Row className="g-0">
					<Col>
						<span className={classNames('border-half-danger', 'd-block', 'h5', !hideProgress && ['mb-md-3', 'pb-md-2'], 'text-center')}>{nextVoucher.name}</span>
					</Col>
					{ctaAnchor && (
						<Col xs="12" md="6">
							<GamifiedButton as={Button} variant="outline-danger" size="sm" className="w-100" tag="a" href={ctaAnchor} onClick={handleClickCTA}>
								<FormattedMessage id="voucherButtonCTA" defaultMessage="Take advantage of the offer" /> 🎁
							</GamifiedButton>
						</Col>
					)}
				</Row>
                {!hideProgress && (
					<Row>
						{!onCart && nextVoucherProductAction && (
							<Col xs="12" md={{ span: 4, order: 2 }} lg={{ span: 3, order: 2 }}>
								<div className="position-relative mt-2 mb-1 mb-md-0 text-center">
									<img src={getFile(((products.find(product => product._id === nextVoucherProductAction.value) || {}).pictures || []).find(picture => picture.includes('jpg')), { width: 100 })} alt="Product offer" role="presentation" className="mw-100 w-75 w-md-100 h-auto rounded" width="150" height="100" loading="lazy" />
									<span className="position-absolute h4 text-success text-uppercase d-none d-md-block" style={{ top: '-15px', right: '-15px' }}><Icon icon="gift" size="9x" /></span>
								</div>
							</Col>
						)}
						<Col md={{ span: nextVoucherProductAction ? 8 : 12, order: 1 }} lg={{ span: nextVoucherProductAction ? 9 : 12, order: 1 }} className="pt-md-3">
							{nextVoucherStep > 0 && (
								<div className="d-block text-center text-md-left">
									<strong className="">
										{
											nextVoucherStep > cartTotals.items
											? <FormattedMessage id="voucherProductOfferThreshold" defaultMessage="Only {amount} left to take advantage of the offer!" values={{ amount: <Price amount={nextVoucherStep - cartTotals.items} /> }} />
											: <FormattedMessage id="voucherProductOfferSuccess" defaultMessage="Great ! You get the offer!" />
										}
									</strong>
								</div>
							)}
							{nextVoucherStep > 0 && cartTotals.items > 0 && (
								<ProgressBar
									animated
									striped
									variant="danger"
									size="sm"
									min="20"
									now={(cartTotals.items * 100) / nextVoucherStep}
									style={{ fontSize: '0.8rem' /* , height: '2rem', lineHeight: '2rem' */ }}
									label={<span className="text-white"><strong><Price amount={cartTotals.items} /></strong> / <Price amount={nextVoucherStep} /></span>}
								/>
							)}

						</Col>
					</Row>
				)}
                {
					// !hideProgress && (
						(displayConditions || cartTotals.items === 0 || nextVoucherStep === 0)
						? (
							<div className="mt-2 ps-2 rounded" style={{ fontSize: '0.8rem', borderLeft: '4px solid var(--bs-danger)' }}>
								<HtmlComponent text={nextVoucher.description} />
							</div>
						) : <em className="d-block text-end" role="button" tabIndex={0} onClick={setDisplayConditions} onKeyDown={e => e.key === 'Enter' && setDisplayConditions(true)}>{' '}<FormattedMessage id="learnMoreButton" defaultMessage="Learn more" /></em>
					// )
                }
            </div>
        );
    }
	if(process.env.NODE_ENV === 'development') {
		return <span>{nextVoucher?.label}: conditions {!checkVoucherConditions(nextVoucher, user, userGroup, orders, cart) && 'not'} checked</span>;
	}
    return null;
}

function mapStateToProps(store, props) {
    const user = getLoggedUser(store);
    return {
        user,
		userGroup: getUserGroup(store),
        cart: getCart(store),
        cartTotals: getCartTotals(store),
        cartVouchers: getCartVouchers(store),
        vouchers: getVouchers(store, true).filter(voucher => checkVoucherConditions({ ...voucher, conditions: voucher.conditions.filter(condition => condition.key !== 'subtotal') }, user, getUserGroup(store), user && getOrdersByUser(store, user._id, ['onpreparation', 'shipped', 'complete']), getCart(store))),
        products: memoizedGetProducts(store, { isActive: true }),
		orders: user ? getOrdersByUser(store, user._id) : [],
    };
}

VoucherProgress.propTypes = {
    dispatch: PropTypes.func.isRequired,
	user: PropTypes.object,
	userGroup: PropTypes.object,
    cart: PropTypes.object,
    cartTotals: PropTypes.object,
    cartVouchers: PropTypes.arrayOf(PropTypes.object),
    vouchers: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.object),
	orders: PropTypes.arrayOf(PropTypes.object),
	onCart: PropTypes.bool,
	hideProgress: PropTypes.bool,
	ctaAnchor: PropTypes.string,
};

VoucherProgress.defaultProps = {
	ctaAnchor: null,
};

export default connect(mapStateToProps)(VoucherProgress);
